import type { HapticType, PlatformAdapter } from '@blockbattle/platform-adapters';

export type GameHaptic =
  | 'navigate'
  | 'select'
  | 'gameStart'
  | 'place'
  | 'clear'
  | 'multiClear'
  | 'combo2'
  | 'combo3'
  | 'comboBig'
  | 'comboBreak'
  | 'record'
  | 'reward'
  | 'energy'
  | 'duelChallenge'
  | 'win'
  | 'lose'
  | 'draw'
  | 'purchase'
  | 'gift'
  | 'chat'
  | 'error'
  | 'artifactLaser'
  | 'artifactRocket'
  | 'artifactPlasma'
  | 'artifactImpulse'
  | 'artifactReroll'
  | 'artifactUndo';

export interface HapticStep {
  readonly type: HapticType;
  readonly delay: number;
}

const patterns: Readonly<Record<GameHaptic, readonly HapticStep[]>> = {
  navigate: [{ type: 'light', delay: 0 }],
  select: [{ type: 'light', delay: 0 }],
  gameStart: [
    { type: 'light', delay: 0 },
    { type: 'medium', delay: 90 },
  ],
  place: [{ type: 'medium', delay: 0 }],
  clear: [{ type: 'success', delay: 0 }],
  multiClear: [
    { type: 'medium', delay: 0 },
    { type: 'success', delay: 85 },
  ],
  combo2: [
    { type: 'light', delay: 0 },
    { type: 'success', delay: 70 },
  ],
  combo3: [
    { type: 'medium', delay: 0 },
    { type: 'medium', delay: 65 },
    { type: 'success', delay: 130 },
  ],
  comboBig: [
    { type: 'heavy', delay: 0 },
    { type: 'success', delay: 90 },
    { type: 'heavy', delay: 175 },
  ],
  comboBreak: [
    { type: 'light', delay: 0 },
    { type: 'light', delay: 75 },
  ],
  record: [
    { type: 'heavy', delay: 0 },
    { type: 'success', delay: 110 },
  ],
  reward: [{ type: 'success', delay: 0 }],
  energy: [
    { type: 'light', delay: 0 },
    { type: 'medium', delay: 90 },
  ],
  duelChallenge: [
    { type: 'heavy', delay: 0 },
    { type: 'medium', delay: 140 },
  ],
  win: [
    { type: 'success', delay: 0 },
    { type: 'heavy', delay: 120 },
  ],
  lose: [
    { type: 'heavy', delay: 0 },
    { type: 'light', delay: 130 },
  ],
  draw: [
    { type: 'medium', delay: 0 },
    { type: 'medium', delay: 115 },
  ],
  purchase: [
    { type: 'medium', delay: 0 },
    { type: 'success', delay: 90 },
  ],
  gift: [
    { type: 'light', delay: 0 },
    { type: 'medium', delay: 75 },
    { type: 'success', delay: 150 },
  ],
  chat: [{ type: 'light', delay: 0 }],
  error: [{ type: 'error', delay: 0 }],
  artifactLaser: [
    { type: 'medium', delay: 0 },
    { type: 'light', delay: 55 },
  ],
  artifactRocket: [
    { type: 'heavy', delay: 0 },
    { type: 'success', delay: 145 },
  ],
  artifactPlasma: [
    { type: 'light', delay: 0 },
    { type: 'medium', delay: 55 },
    { type: 'success', delay: 120 },
  ],
  artifactImpulse: [
    { type: 'heavy', delay: 0 },
    { type: 'medium', delay: 75 },
    { type: 'heavy', delay: 150 },
  ],
  artifactReroll: [
    { type: 'light', delay: 0 },
    { type: 'light', delay: 65 },
    { type: 'medium', delay: 130 },
  ],
  artifactUndo: [
    { type: 'medium', delay: 0 },
    { type: 'light', delay: 70 },
    { type: 'light', delay: 135 },
  ],
};

const lastPlayedAt = new Map<GameHaptic, number>();

export function hapticPattern(event: GameHaptic): readonly HapticStep[] {
  return patterns[event];
}

export function moveHaptic(input: {
  readonly isRecord: boolean;
  readonly clearedLines: number;
  readonly combo: number;
  readonly comboBroken: boolean;
}): GameHaptic {
  if (input.isRecord) return 'record';
  if (input.combo >= 4) return 'comboBig';
  if (input.combo === 3) return 'combo3';
  if (input.combo === 2) return 'combo2';
  if (input.clearedLines > 1) return 'multiClear';
  if (input.clearedLines === 1) return 'clear';
  if (input.comboBroken) return 'comboBreak';
  return 'place';
}

export function playGameHaptic(
  adapter: PlatformAdapter | null,
  enabled: boolean,
  event: GameHaptic,
): void {
  if (
    !enabled ||
    !adapter ||
    typeof adapter.haptic !== 'function' ||
    document.visibilityState === 'hidden'
  )
    return;
  const now = Date.now();
  if (now - (lastPlayedAt.get(event) ?? 0) < 70) return;
  lastPlayedAt.set(event, now);
  for (const step of patterns[event]) {
    const vibrate = (): void => {
      void Promise.resolve()
        .then(() => adapter.haptic(step.type))
        .catch(() => undefined);
    };
    if (step.delay === 0) vibrate();
    else window.setTimeout(vibrate, step.delay);
  }
}
